/* eslint-disable react/destructuring-assignment */
import { DataSource } from 'hooks/useHttpClient';
import {
    LegacyAddress,
    FactAudit,
    AddressResolver,
    LegacyAddressResolver,
    Address,
    PaginationMeta,
    PaginationMetaResolver
} from './Common';

export type PaginatedUserList = {
    data: FullUser[];
    meta: PaginationMeta;
};

export type User = {
    id: number;
    email: string;
    avatar?: string;
    bio?: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    jobTitle?: string;
    department?: string;
    active?: boolean;
    internal?: boolean;
    invitationAccepted: boolean;
};

export type FullUser = {
    id: string;
    hyrId: number;
    firstName: string;
    lastName: string;
    bio: string | null;
    email: string;
    jobTitle: string;
    department: string;
    phoneNumber: string | null;
    legacyLocation: LegacyAddress | null;
    location: Address | null;
    avatarUrl: string | null;
    companies: {
        id: number;
        name: string;
        avatarUrl: string | null;
    }[];
    createdBy: FactAudit | null;
    internal: boolean;
    active: boolean;
    invitationAccepted: boolean;
    pendingAgreement?:
        | {
              id: number;
              name: string;
              fileUrl: string;
          }
        | undefined;
};

export function UserSingleMemberResolver(dataSource: DataSource, apiResponse: any): User | null {
    if (!apiResponse) return null;
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    email: apiResponse.email,
                    avatar: cleanAvatarUrl(apiResponse.profile_picture || apiResponse.profile?.profile_picture),
                    fullName: apiResponse.name || `${apiResponse.first_name} ${apiResponse.last_name}`,
                    firstName: apiResponse.first_name || apiResponse.profile?.first_name,
                    lastName: apiResponse.last_name || apiResponse.profile?.last_name,
                    jobTitle: apiResponse.job_title || apiResponse.profile?.job_title,
                    active: apiResponse.active,
                    internal: apiResponse.internal,
                    phone: apiResponse.location?.phone_number || undefined,
                    invitationAccepted: apiResponse.invitationAccepted || false
                } as User;

            default:
                return null;
        }
    } catch (error) {
        console.error(error);
        return null;
    }
}

export const SingleFullUserResolver = (dataSource: DataSource, apiResponse: any): FullUser | null => {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    hyrId: apiResponse.id,
                    firstName: apiResponse.first_name,
                    lastName: apiResponse.last_name,
                    bio: apiResponse.bio,
                    email: apiResponse.email,
                    jobTitle: apiResponse.job_title,
                    department: apiResponse.department,
                    phoneNumber: apiResponse.location?.phone_number || null,
                    legacyLocation: LegacyAddressResolver(dataSource, apiResponse.location),
                    location: AddressResolver(dataSource, apiResponse.location),
                    avatarUrl: cleanAvatarUrl(apiResponse.profile_picture),
                    active: apiResponse.active,
                    internal: apiResponse.internal,
                    companies:
                        apiResponse.company?.map((item: any) => ({
                            id: item.id,
                            name: item.name,
                            avatarUrl: cleanAvatarUrl(item.logo)
                        })) || [],
                    createdBy: apiResponse.created_by
                        ? {
                              userId: apiResponse.created_by.id,
                              userFullName: `${apiResponse.created_by.first_name} ${apiResponse.created_by.last_name}`,
                              avatar: cleanAvatarUrl(apiResponse.created_by.profile_picture),
                              dateStr: ''
                          }
                        : null,
                    invitationAccepted: apiResponse.invitationAccepted || false,
                    pendingAgreement: apiResponse.agreement
                        ? {
                              id: apiResponse.agreement.id,
                              name: apiResponse.agreement.name,
                              fileUrl: apiResponse.agreement.document_url
                          }
                        : undefined
                } as FullUser;

            default:
                return null;
        }
    } catch (error) {
        console.log('SingleUserResolver error', error);
        return null;
    }
};

export function UserListResolver(dataSource: DataSource, apiResponse: any): PaginatedUserList | null {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    data: apiResponse.data.map((user: any) => SingleFullUserResolver(dataSource, user)).filter(Boolean),
                    meta: PaginationMetaResolver(dataSource, apiResponse.meta) as PaginationMeta
                };

            default:
                return null;
        }
    } catch (error) {
        console.log(error);
        return null;
    }
}

function cleanAvatarUrl(avatarUrl: string | undefined): string {
    const storageUrl = process.env.REACT_APP_STORAGE_BASE_URL || 'https://apidev.hyrerocket.com/storage';
    if (!avatarUrl) {
        return '';
    }

    if (avatarUrl.startsWith('http')) {
        return avatarUrl;
    }

    if (!avatarUrl.startsWith('/')) {
        avatarUrl = `/${avatarUrl}`;
    }

    return `${storageUrl}${avatarUrl}`;
}
